import { Theme } from '@mui/system';
import {
  AppThunk,
  logUserActivity,
  useAppDispatch,
  useAppSelector,
  UserActivityAction,
  UserActivityActionSummary,
  UserActivityPageName,
} from 'common';
import { useState } from 'react';

import { StyledMenuLabel } from '../../../../cf-ui/Drawer/CfDrawer.styles';
import { CfUncontrolledDrawer } from '../../../../cf-ui/Drawer/CfUncontrolledDrawer';
import { CfDrawerHeader } from '../../../../cf-ui/Drawer/components/CfDrawerHeader';
import { CfActionMenu } from '../../../../cf-ui/Menu/CfActionMenu';
import { CfMenuItem } from '../../../../cf-ui/Menu/MenuItem/CfMenuItem';
import { DeviceType, useBreakpoint } from '../../../../hooks/useBreakpoint';
import RecipePrivateUsersDialog, {
  RecipePrivateUsersDialogProps,
} from '../../../recipes/components/RecipePrivateUsersDialog/RecipePrivateUsersDialog';
import { toggleSecretRecipeRowActions, useSecretRecipeRowAction } from './useSecretRecipeRowAction';
import { getResolution } from '../../../../utilities/resolution';

enum MenuItemKeyType {
  ManageUsers = 'manage-users',
}

export const useSecretRecipeRowActionKeys = (): Set<string> => {
  return new Set([MenuItemKeyType.ManageUsers]);
};

export const SecretRecipeRowActionsMenu = ({
  setRefreshUser,
}: {
  setRefreshUser: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const { deviceType } = useBreakpoint();
  const rowActionState = useSecretRecipeRowAction();
  const dispatch = useAppDispatch();
  const sxProps = {
    height: (theme: Theme) => ({ xs: theme.spacing(5.5), xl: theme.spacing(4.5) }),
    display: 'flex',
    alignItems: 'center',
    py: 0,
  };

  const customerId = useAppSelector((s) => s.customer.selectedCustomer?.CustomerId) || '';

  const [privateUsers, setPrivateUsers] = useState<RecipePrivateUsersDialogProps | undefined>(undefined);

  const anchor = rowActionState?.anchor;
  const recipeHeader = rowActionState?.recipeHeader;
  const recipeId = recipeHeader?.RecipeHeaderId;

  const menuItemKeySet = useSecretRecipeRowActionKeys();

  const closeMenu = () => {
    toggleSecretRecipeRowActions();
  };

  const menuItems = [
    <CfMenuItem
      key={MenuItemKeyType.ManageUsers}
      onSelect={() => {
        closeMenu();
        if (recipeId) {
          setPrivateUsers({
            customerId,
            recipeId,
            onClose: () => {
              setRefreshUser(false);
              setPrivateUsers(undefined);
            },
            onSuccess: () => {
              dispatch(
                logUserActivity({
                  action: UserActivityAction.ManagePrivateRecipeUsers,
                  pageName: UserActivityPageName.ManageRecipeAccess,
                  actionSummary: UserActivityActionSummary.ManagePrivateRecipeUsers,
                  resolution: getResolution(),
                })
              );
            },
            setRefreshUser,
          });
        }
      }}
      sx={{ ...sxProps }}
    >
      <StyledMenuLabel whiteSpace='nowrap'>Manage secret recipe editors</StyledMenuLabel>
    </CfMenuItem>,
  ].filter((n) => typeof n.key === 'string' && menuItemKeySet.has(n.key as string));

  return (
    <>
      {deviceType === DeviceType.Desktop ? (
        <CfActionMenu anchorElement={anchor} onClose={closeMenu} width={232}>
          {menuItems}
        </CfActionMenu>
      ) : (
        <CfUncontrolledDrawer
          open={Boolean(anchor)}
          onClose={closeMenu}
          mode='menu'
          keepMounted
          placement='bottom'
          displayAboveHeader
          prefix={<CfDrawerHeader onClose={closeMenu}>Actions</CfDrawerHeader>}
        >
          {menuItems}
        </CfUncontrolledDrawer>
      )}
      {privateUsers && <RecipePrivateUsersDialog {...privateUsers} />}
    </>
  );
};
